import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { DashboardLayout } from '../components/layout/DashboardLayout';
import { playersAPI } from '../lib/api';
import { toast } from 'sonner';
import { ArrowLeft, Loader2, Save } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';

const positions = [
  { value: 'GK', label: 'Portera' },
  { value: 'DEF', label: 'Defensa' },
  { value: 'MID', label: 'Mediocampista' },
  { value: 'FWD', label: 'Delantera' },
];

const availabilities = [
  { value: 'transfer', label: 'Transferencia' },
  { value: 'loan', label: 'Préstamo' },
  { value: 'both', label: 'Ambos (Transferencia o Préstamo)' },
];

const feet = [
  { value: 'right', label: 'Diestra' },
  { value: 'left', label: 'Zurda' },
  { value: 'both', label: 'Ambidiestra' },
];

export const AddPlayerPage = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    position: '',
    age: '',
    nationality: '',
    current_club_name: '',
    contract_until: '',
    availability: '',
    asking_price: '',
    salary_expectation: '',
    height: '',
    preferred_foot: '',
    languages: '',
    experience_years: '',
    international_caps: '',
    description: '',
    video_url: '',
    photo_url: '',
  });

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.name || !formData.position || !formData.age || !formData.nationality || !formData.availability) {
      toast.error('Por favor completa los campos obligatorios');
      return;
    }

    setLoading(true);
    try {
      const data = {
        ...formData,
        age: parseInt(formData.age),
        asking_price: formData.asking_price ? parseFloat(formData.asking_price) : null,
        salary_expectation: formData.salary_expectation ? parseFloat(formData.salary_expectation) : null,
        height: formData.height ? parseInt(formData.height) : null,
        experience_years: formData.experience_years ? parseInt(formData.experience_years) : null,
        international_caps: formData.international_caps ? parseInt(formData.international_caps) : null,
        languages: formData.languages ? formData.languages.split(',').map(l => l.trim()) : null,
      };
      
      await playersAPI.create(data);
      toast.success('Jugadora publicada correctamente');
      navigate('/my-publications');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al publicar la jugadora');
    } finally {
      setLoading(false);
    }
  };

  return (
    <DashboardLayout>
      <div className="max-w-3xl mx-auto" data-testid="add-player-page">
        <Button
          variant="ghost"
          onClick={() => navigate(-1)}
          className="mb-6"
        >
          <ArrowLeft className="w-4 h-4 mr-2" />
          Volver
        </Button>

        <div className="bg-white rounded-xl border border-slate-200 p-6 md:p-8">
          <h1 className="font-heading text-2xl font-bold text-slate-800 uppercase tracking-tight mb-6">
            Publicar Jugadora Disponible
          </h1>

          <form onSubmit={handleSubmit} className="space-y-6">
            {/* Required fields */}
            <div className="space-y-4">
              <h2 className="font-heading text-sm font-semibold text-slate-500 uppercase tracking-wider border-b border-slate-200 pb-2">
                Información Básica *
              </h2>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="md:col-span-2">
                  <Label className="form-label">Nombre completo *</Label>
                  <Input
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    placeholder="Nombre de la jugadora"
                    required
                    data-testid="player-name"
                  />
                </div>

                <div>
                  <Label className="form-label">Posición *</Label>
                  <Select
                    value={formData.position}
                    onValueChange={(value) => setFormData({ ...formData, position: value })}
                    required
                  >
                    <SelectTrigger data-testid="player-position">
                      <SelectValue placeholder="Seleccionar posición" />
                    </SelectTrigger>
                    <SelectContent>
                      {positions.map((pos) => (
                        <SelectItem key={pos.value} value={pos.value}>
                          {pos.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div>
                  <Label className="form-label">Edad *</Label>
                  <Input
                    type="number"
                    value={formData.age}
                    onChange={(e) => setFormData({ ...formData, age: e.target.value })}
                    placeholder="25"
                    min="15"
                    max="50"
                    required
                    data-testid="player-age"
                  />
                </div>

                <div>
                  <Label className="form-label">Nacionalidad *</Label>
                  <Input
                    value={formData.nationality}
                    onChange={(e) => setFormData({ ...formData, nationality: e.target.value })}
                    placeholder="España"
                    required
                    data-testid="player-nationality"
                  />
                </div>

                <div>
                  <Label className="form-label">Disponibilidad *</Label>
                  <Select
                    value={formData.availability}
                    onValueChange={(value) => setFormData({ ...formData, availability: value })}
                    required
                  >
                    <SelectTrigger data-testid="player-availability">
                      <SelectValue placeholder="Tipo de operación" />
                    </SelectTrigger>
                    <SelectContent>
                      {availabilities.map((av) => (
                        <SelectItem key={av.value} value={av.value}>
                          {av.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>
            </div>

            {/* Optional fields */}
            <div className="space-y-4">
              <h2 className="font-heading text-sm font-semibold text-slate-500 uppercase tracking-wider border-b border-slate-200 pb-2">
                Información Adicional (Opcional)
              </h2>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <Label className="form-label">Club actual</Label>
                  <Input
                    value={formData.current_club_name}
                    onChange={(e) => setFormData({ ...formData, current_club_name: e.target.value })}
                    placeholder="Nombre del club"
                    data-testid="player-current-club"
                  />
                </div>

                <div>
                  <Label className="form-label">Fin de contrato</Label>
                  <Input
                    value={formData.contract_until}
                    onChange={(e) => setFormData({ ...formData, contract_until: e.target.value })}
                    placeholder="Junio 2025"
                    data-testid="player-contract"
                  />
                </div>

                <div>
                  <Label className="form-label">Precio pedido (€)</Label>
                  <Input
                    type="number"
                    value={formData.asking_price}
                    onChange={(e) => setFormData({ ...formData, asking_price: e.target.value })}
                    placeholder="50000"
                    data-testid="player-price"
                  />
                </div>

                <div>
                  <Label className="form-label">Expectativa salarial (€/año)</Label>
                  <Input
                    type="number"
                    value={formData.salary_expectation}
                    onChange={(e) => setFormData({ ...formData, salary_expectation: e.target.value })}
                    placeholder="30000"
                    data-testid="player-salary"
                  />
                </div>

                <div>
                  <Label className="form-label">Altura (cm)</Label>
                  <Input
                    type="number"
                    value={formData.height}
                    onChange={(e) => setFormData({ ...formData, height: e.target.value })}
                    placeholder="170"
                    data-testid="player-height"
                  />
                </div>

                <div>
                  <Label className="form-label">Pie preferido</Label>
                  <Select
                    value={formData.preferred_foot}
                    onValueChange={(value) => setFormData({ ...formData, preferred_foot: value })}
                  >
                    <SelectTrigger data-testid="player-foot">
                      <SelectValue placeholder="Seleccionar" />
                    </SelectTrigger>
                    <SelectContent>
                      {feet.map((f) => (
                        <SelectItem key={f.value} value={f.value}>
                          {f.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div>
                  <Label className="form-label">Años de experiencia</Label>
                  <Input
                    type="number"
                    value={formData.experience_years}
                    onChange={(e) => setFormData({ ...formData, experience_years: e.target.value })}
                    placeholder="5"
                    data-testid="player-experience"
                  />
                </div>

                <div>
                  <Label className="form-label">Internacionalidades</Label>
                  <Input
                    type="number"
                    value={formData.international_caps}
                    onChange={(e) => setFormData({ ...formData, international_caps: e.target.value })}
                    placeholder="10"
                    data-testid="player-caps"
                  />
                </div>

                <div className="md:col-span-2">
                  <Label className="form-label">Idiomas (separados por coma)</Label>
                  <Input
                    value={formData.languages}
                    onChange={(e) => setFormData({ ...formData, languages: e.target.value })}
                    placeholder="Español, Inglés, Francés"
                    data-testid="player-languages"
                  />
                </div>

                <div className="md:col-span-2">
                  <Label className="form-label">Descripción / Notas</Label>
                  <Textarea
                    value={formData.description}
                    onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                    placeholder="Información adicional sobre la jugadora..."
                    rows={4}
                    data-testid="player-description"
                  />
                </div>

                <div>
                  <Label className="form-label">URL de foto</Label>
                  <Input
                    value={formData.photo_url}
                    onChange={(e) => setFormData({ ...formData, photo_url: e.target.value })}
                    placeholder="https://..."
                    data-testid="player-photo"
                  />
                </div>

                <div>
                  <Label className="form-label">URL de video</Label>
                  <Input
                    value={formData.video_url}
                    onChange={(e) => setFormData({ ...formData, video_url: e.target.value })}
                    placeholder="https://youtube.com/..."
                    data-testid="player-video"
                  />
                </div>
              </div>
            </div>

            {/* Submit */}
            <div className="flex justify-end gap-4 pt-4 border-t border-slate-200">
              <Button
                type="button"
                variant="outline"
                onClick={() => navigate(-1)}
              >
                Cancelar
              </Button>
              <Button
                type="submit"
                className="bg-indigo-600 hover:bg-indigo-700"
                disabled={loading}
                data-testid="submit-player"
              >
                {loading ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Publicando...
                  </>
                ) : (
                  <>
                    <Save className="w-4 h-4 mr-2" />
                    Publicar Jugadora
                  </>
                )}
              </Button>
            </div>
          </form>
        </div>
      </div>
    </DashboardLayout>
  );
};

export default AddPlayerPage;
